<i18n>
{
  "ja": {
    "title": "よくあるご質問",
    "description": "倉庫のへの一括問い合わせから入出荷管理までできる倉庫利用サービス「WareX」へのよくある質問をまとめてご紹介しています。",
    "more": "さらに詳しく知りたい方は、{newline-sp}以下のページからお問い合わせください。",
    "btn": {
      "contact": "お問い合わせはこちら"
    }
  },
  "en": {
    "title": "FAQ"
  }
}
</i18n>

<template>
  <div>
    <header class="my-16 md:mb-32 text-center w-7/8 mx-auto">
      <h1 class="block-header">{{ $t("title") }}</h1>
      <p class="ornament-header">{{ $t("title", "en") }}</p>
    </header>

    <div class="md:flex justify-between max-w-content mx-auto mb-16 md:mb-24 px-6 md:px-12">
      <div ref="nav" v-if="categories && categories.length" class="md:w-3/12">
        <ul class="flex justify-between flex-wrap md:block md:rounded-lg overflow-hidden">
          <li
            v-for="(cat, index) in categories"
            :key="index"
            :class="[index && 'md:mt-1']"
            class="w-48p md:w-full mt-3 md:mt-0"
          >
            <button
              :to="{ query: { category: cat.slug } }"
              :class="[isCurrentCategory(cat.slug) ? 'bg-secondary text-white' : 'bg-gray-200 text-gray-600 hover:bg-secondary-lighter hover:bg-opacity-25']"
              class="block w-full text-sm md:text-base p-3 text-center md:text-left md:px-6 block leading-tight overflow-hidden rounded-lg md:rounded-none"
              v-text="cat.name"
              @click.prevent="onChangeCategory(cat.slug)"
            />
          </li>
        </ul>
      </div>
      <div ref="list" v-if="faqs && faqs.length" class="md:w-8/12">
        <h2 v-if="filterCategory" class="mt-16 md:-mt-12 mb-5 text-xl text-black text-center md:text-left font-semibold">{{ filterCategory.name }}</h2>
        <ul>
          <li v-for="(qa, index) in faqs" :key="index" :class="[!index && 'border-t']" class="border-solid border-b border-gray-200">
            <button
              class="pt-6 pb-8 pr-12 md:pr-20 text-left relative w-full flex relative hover:text-secondary-light"
              :class="[faqAcdOpened.includes(index) ? 'text-secondary-light' : 'text-black']"
              @click="onOpen(index)"
            >
              {{ qa.head }}
              <i
                :class="[faqAcdOpened.includes(index) ? 'border-r-2 border-t-2 mt-8' : 'border-l-2 border-b-2']"
                class="block ml-auto mt-6 mr-6 w-3 h-3 border-black transform -rotate-45 absolute top-0 right-0"
              />
            </button>
            <div :class="{ 'hidden': !faqAcdOpened.includes(index) }" class="pr-12 md:pr-20">
              <div class="md text-black text-sm pb-8" v-html="$md.render(qa.body)" />
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div class="text-center my-16 md:my-24 w-7/8 lg:w-auto mx-auto">
      <i18n path="more" tag="p">
        <template #newline-sp><br class="md:hidden"></template>
      </i18n>
      <div class="md:mt-4 mt-8 mb-12">
        <i class="block mx-auto w-3 h-3 transform -rotate-45 border-black border-l-2 border-b-2" />
        <i class="block mx-auto -mt-1 w-3 h-3 transform -rotate-45 border-black border-l-2 border-b-2" />
      </div>
      <p>
        <nuxt-link :to="localePath('contact')" class="inline-flex items-center justify-center w-7/8 max-w-xs bg-accent text-white py-4 px-4 rounded-full text-center">
          {{ $t("btn.contact") }}
          <span class="material-icons align-middle ml-4">arrow_forward</span>
        </nuxt-link>
      </p>
    </div>

    <contact />
    <BreadcrumbsFoot :list="breadcrumbs" />
  </div>
</template>

<style lang="sass" scoped>
@import '~assets/css/markdown.sass'
</style>

<script>
import contact from '@/components/contact';
import BreadcrumbsFoot from "@/components/breadcrumbsFoot";
import Jsonld from '@/components/mixins/jsonld';

export default {
  mixins: [Jsonld],
  components: { contact, BreadcrumbsFoot },
  data() {
    return {
      faqAcdOpened: [],
      breadcrumbs: [
        { name: this.$t('title') }
      ]
    }
  },
  async asyncData({ app, store }) {
    try {
      await store.dispatch("cms/faqs")
      await store.dispatch("cms/faqCategories")
    } catch(e) {
      // errror handling can be added here if needed
    }
    return {
      faqItems: store.getters['cms/faqs'],
      faqCategories: store.getters['cms/faqCategories'] || []
    }
  },
  computed: {
    faqs() {
      const category = this.filterCategory
      return category && this.faqItems.filter((e) => e.faq_categories.some(c => c.slug === category.slug)) || []
    },
    categories() {
      return this.faqCategories.filter(e => e.faqs.length)
    },
    filterCategory() {
      const { category } = this.$route.query || {}
      return this.getCategory(category) || null
    },
    global() {
      return this.$store.getters['cms/global']
    },
    homepage() {
      return this.$store.getters['cms/homepage']
    }
  },
  watch: {
    '$route.query.category'(val) {
      this.resetFaqAcd()
    }
  },
  methods: {
    isSP() {
      const { nav, list } = this.$refs
      const { top: listTop } = this.getRect(list)
      const { top: navTop } = this.getRect(nav)
      return listTop !== navTop
    },
    getRect(el) {
      return el && el.getBoundingClientRect() || {}
    },
    isCurrentCategory(slug) {
      const category = this.filterCategory
      return slug === category.slug
    },
    resetFaqAcd() {
      this.faqAcdOpened = []
    },
    onChangeCategory(slug) {
      if (this.isSP()) {
        this.$refs.list && this.$scrollTo(this.$refs.list)
      }
      this.$router.push({ query: { category: slug } })
    },
    onOpen(i) {
      const id = i
      const index = this.faqAcdOpened.indexOf(id)
      if (index > -1) {
        this.faqAcdOpened.splice(index, 1)
      } else {
        this.faqAcdOpened.push(id)
      }
    },
    getCategory(cat) {
      const { categories } = this
      const category = categories && categories.filter(e => e.slug === cat) || []
      return category.length ? category[0] : categories.length && categories[0]
    }
  },
  head() {
    const { seo } = this.homepage;
    const { defaultSeo } = this.global;
    const fullSeo = {
      ...defaultSeo,
      ...seo,
      metaTitle: this.$t('title'),
      metaDescription: this.$t('description'),
      metaKeywords: `${this.$t('title')},${this.$t('keywords')}`
    };
    return {
      title: fullSeo.metaTitle,
      meta: this.$options.filters.cmsMetaTags(fullSeo)
    }
  },
  jsonld() {
    const ret = []
    const breadcrumbs = this.getBreadcrumbsJsonld(this.breadcrumbs)
    breadcrumbs && ret.push(breadcrumbs)
    return ret
  }
};
</script>
